// Copyright ©️ 2026 eVolve MEP, LLC

import { Combobox, type Primitive, isPrimitive } from '@mantine/core';

import { creatorKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import type { WithRequiredIsh } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

import { SelectOption, normalizeSelectOption } from './SelectOption';
import type {
  TypeSafeComboboxItem,
  TypeSafeData,
  TypeSafeGroupComboboxData,
  WrappedMultiSelectProps,
  WrappedSelectProps,
} from './types';

const normalizeForComparison = <T extends Primitive>(value: T) => String(value).toLowerCase().trim();

const isGroup = <T extends Primitive, D>(
  item: T | TypeSafeComboboxItem<T, D> | TypeSafeGroupComboboxData<T, D>,
): item is TypeSafeGroupComboboxData<T, D> => !isPrimitive(item) && 'group' in item && 'items' in item;

export const getSelectOptionLabel = <T extends Primitive, D extends object>(
  data: TypeSafeData<T, D>,
  value: T,
): string | null => {
  for (const item of data) {
    if (isPrimitive(item)) {
      if (item === value) {
        return String(item);
      }
      continue;
    }
    if (isGroup(item)) {
      const found = item.items.find((i) => i.value === value);
      if (found) {
        return found.label;
      }
      continue;
    }
    if (item.value === value) return item.label;
  }
  return null;
};

/**
 * Check if `optionValue` is one of `selectedValue`
 *
 * Helper so we can use the same check on single and multi selects
 */
const isChecked = <T extends Primitive>(optionValue: T, selectedValue: T | T[] | null | undefined) =>
  Array.isArray(selectedValue) ? selectedValue.includes(optionValue) : selectedValue === optionValue;

/** Check if option `value` exists in `data` */
export const optionExists = <T extends Primitive, D>(data: TypeSafeData<T, D>, value: T | string): boolean => {
  const trimmedValue = normalizeForComparison(value);
  return data.some((opt) => {
    if (isPrimitive(opt)) {
      return normalizeForComparison(opt) === trimmedValue;
    }
    if ('label' in opt) {
      return normalizeForComparison(opt.label) === trimmedValue || normalizeForComparison(opt.value) === trimmedValue;
    }
    if ('group' in opt) {
      return optionExists(opt.items, value);
    }
    return false;
  });
};

export const getSelectOptions = <T extends Primitive, D extends object>({
  data,
  ...props
}: WithRequiredIsh<
  Pick<
    WrappedSelectProps<T, D> | WrappedMultiSelectProps<T, D>,
    'data' | 'limit' | 'value' | 'hidePickedOptions' | 'renderOption' | 'searchValue' | 'noFilterOnSearch'
  >
>) => {
  const { value, limit, hidePickedOptions, renderOption, searchValue, noFilterOnSearch } = props;

  const shouldHideOption = (option: T | TypeSafeComboboxItem<T, D>) => {
    const { value: optionValue, label } = normalizeSelectOption(option);
    if (
      optionValue !== creatorKey &&
      !noFilterOnSearch &&
      !!searchValue &&
      !label.toLowerCase().includes(searchValue.trim().toLowerCase())
    ) {
      return true;
    }
    return hidePickedOptions && isChecked(optionValue, value);
  };

  return data
    .map((optOrGroup, i) => {
      if (isGroup(optOrGroup)) {
        const group = optOrGroup;
        const options = getSelectOptions({ data: group.items, ...props });
        if (options.length === 0) return null;
        return (
          <Combobox.Group key={typeof group.group === 'string' ? group.group : `group-${i}`} label={group.group}>
            {options}
          </Combobox.Group>
        );
      }
      const option = optOrGroup;
      if (shouldHideOption(option)) return null;

      return isPrimitive(option) ? (
        <SelectOption
          key={String(option)}
          option={option}
          renderOption={renderOption}
          checked={isChecked(option, value)}
        />
      ) : (
        <SelectOption
          key={String(option.value)}
          option={option}
          renderOption={renderOption}
          checked={isChecked(option.value, value)}
        />
      );
    })
    .filter(isNotNil)
    .slice(0, limit);
};
